import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
  GetIdCommand,
} from "@aws-sdk/client-cognito-identity";
import {
  ConfirmForgotPasswordCommand,
  CognitoIdentityProviderClient,
  ForgotPasswordCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import type { AwsCredentialIdentity } from "@aws-sdk/types";
import { getCloudConfig } from "./cloudConfig";

type TokenResponse = {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  tokenType: "Bearer";
  expiresIn: number;
};

type NewPasswordChallengeResponse = {
  challengeName: "NEW_PASSWORD_REQUIRED";
  username: string;
  session: string;
  requiredAttributes: string[];
};

type StoredSession = {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  subject: string;
  accessExpiresAt: number;
  identityId: string;
  credentials: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken: string;
    expiration: number;
  };
};

const LS_KEY = "auth.cloud-session";

let inMemorySession: StoredSession | null = null;

const parseJwtPayload = (token: string): Record<string, unknown> => {
  const parts = token.split(".");
  if (parts.length < 2) {
    return {};
  }

  const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");

  try {
    const decoded = window.atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return {};
  }
};

const getSubjectFromToken = (idToken: string): string => {
  const payload = parseJwtPayload(idToken);
  const sub = payload.sub;
  if (typeof sub === "string" && sub.trim()) {
    return sub;
  }

  const email = payload.email;
  if (typeof email === "string" && email.trim()) {
    return email;
  }

  return "unknown";
};

const getTokenExpiresAt = (accessToken: string): number => {
  const payload = parseJwtPayload(accessToken);
  const exp = payload.exp;
  if (typeof exp === "number") {
    return exp * 1000;
  }
  return Date.now() + 15 * 60 * 1000;
};

const getLoginsMap = (idToken: string): Record<string, string> => {
  const { userPoolRegion, userPoolId } = getCloudConfig();
  return {
    [`cognito-idp.${userPoolRegion}.amazonaws.com/${userPoolId}`]: idToken,
  };
};

const readSession = (): StoredSession | null => {
  if (inMemorySession) {
    return inMemorySession;
  }

  const raw = window.localStorage.getItem(LS_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredSession;
    inMemorySession = parsed;
    return parsed;
  } catch {
    window.localStorage.removeItem(LS_KEY);
    return null;
  }
};

const writeSession = (session: StoredSession): void => {
  inMemorySession = session;
  window.localStorage.setItem(LS_KEY, JSON.stringify(session));
};

const clearSession = (): void => {
  inMemorySession = null;
  window.localStorage.removeItem(LS_KEY);
};

const resolveCredentials = async (idToken: string): Promise<{
  identityId: string;
  credentials: StoredSession["credentials"];
}> => {
  const { identityPoolId, identityPoolRegion } = getCloudConfig();
  const identityClient = new CognitoIdentityClient({ region: identityPoolRegion });
  const logins = getLoginsMap(idToken);

  const getIdRes = await identityClient.send(
    new GetIdCommand({
      IdentityPoolId: identityPoolId,
      Logins: logins,
    }),
  );

  if (!getIdRes.IdentityId) {
    throw new Error("Unable to resolve Cognito IdentityId.");
  }

  const credsRes = await identityClient.send(
    new GetCredentialsForIdentityCommand({
      IdentityId: getIdRes.IdentityId,
      Logins: logins,
    }),
  );

  const creds = credsRes.Credentials;
  if (
    !creds?.AccessKeyId ||
    !creds.SecretKey ||
    !creds.SessionToken ||
    !creds.Expiration
  ) {
    throw new Error("Unable to retrieve temporary AWS credentials.");
  }

  return {
    identityId: getIdRes.IdentityId,
    credentials: {
      accessKeyId: creds.AccessKeyId,
      secretAccessKey: creds.SecretKey,
      sessionToken: creds.SessionToken,
      expiration: creds.Expiration.getTime(),
    },
  };
};

const parseRequiredAttributes = (raw: string | undefined): string[] => {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter((entry): entry is string => typeof entry === "string")
      .map((entry) => entry.replace(/^userAttributes\./, ""));
  } catch {
    return [];
  }
};

const finalizeAuthenticatedSession = async (
  accessToken: string,
  idToken: string,
  refreshToken: string | undefined,
): Promise<TokenResponse> => {
  if (!refreshToken) {
    throw new Error("Refresh token not available.");
  }

  const creds = await resolveCredentials(idToken);
  const subject = getSubjectFromToken(idToken);
  const accessExpiresAt = getTokenExpiresAt(accessToken);

  const session: StoredSession = {
    accessToken,
    idToken,
    refreshToken,
    subject,
    accessExpiresAt,
    identityId: creds.identityId,
    credentials: creds.credentials,
  };

  writeSession(session);

  return {
    accessToken,
    idToken,
    refreshToken,
    tokenType: "Bearer" as const,
    expiresIn: Math.max(1, Math.floor((accessExpiresAt - Date.now()) / 1000)),
  };
};

const authenticate = async (authFlow: "USER_PASSWORD_AUTH" | "REFRESH_TOKEN_AUTH", authParameters: Record<string, string>) => {
  const { userPoolClientId, userPoolRegion } = getCloudConfig();
  const providerClient = new CognitoIdentityProviderClient({
    region: userPoolRegion,
  });

  const auth = await providerClient.send(
    new InitiateAuthCommand({
      AuthFlow: authFlow,
      ClientId: userPoolClientId,
      AuthParameters: authParameters,
    }),
  );

  if (auth.ChallengeName === "NEW_PASSWORD_REQUIRED") {
    const username = auth.ChallengeParameters?.USER_ID_FOR_SRP ?? authParameters.USERNAME;
    if (!username || !auth.Session) {
      throw new Error("Cognito challenge session is missing.");
    }

    return {
      challengeName: "NEW_PASSWORD_REQUIRED",
      username,
      session: auth.Session,
      requiredAttributes: parseRequiredAttributes(
        auth.ChallengeParameters?.requiredAttributes,
      ),
    } satisfies NewPasswordChallengeResponse;
  }

  if (!auth.AuthenticationResult?.AccessToken || !auth.AuthenticationResult.IdToken) {
    throw new Error("Cognito authentication failed.");
  }

  const accessToken = auth.AuthenticationResult.AccessToken;
  const idToken = auth.AuthenticationResult.IdToken;
  const refreshToken =
    auth.AuthenticationResult.RefreshToken ?? authParameters.REFRESH_TOKEN;

  return finalizeAuthenticatedSession(
    accessToken,
    idToken,
    refreshToken,
  );
};

const isCredentialsValid = (session: StoredSession): boolean => {
  return session.credentials.expiration > Date.now() + 30_000;
};

const refreshCredentialsFromRefreshToken = async (): Promise<StoredSession> => {
  const session = readSession();
  if (!session?.refreshToken) {
    throw new Error("No refresh token available.");
  }

  await authenticate("REFRESH_TOKEN_AUTH", {
    REFRESH_TOKEN: session.refreshToken,
  });

  const next = readSession();
  if (!next) {
    throw new Error("Failed to refresh cloud auth session.");
  }

  return next;
};

const getRequiredTrimmedString = (value: unknown, fieldName: string): string => {
  if (typeof value !== "string") {
    throw new Error(`${fieldName} is required.`);
  }

  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`${fieldName} is required.`);
  }

  return trimmed;
};

export const cloudAuthSession = {
  signInWithUsernamePassword: async (
    username: string,
    password: string,
  ): Promise<TokenResponse | NewPasswordChallengeResponse> => {
    const normalizedUsername = getRequiredTrimmedString(username, "Username");
    const normalizedPassword = getRequiredTrimmedString(password, "Password");

    return authenticate("USER_PASSWORD_AUTH", {
      USERNAME: normalizedUsername,
      PASSWORD: normalizedPassword,
    });
  },

  completeNewPasswordChallenge: async (args: {
    username: string;
    newPassword: string;
    session: string;
  }): Promise<TokenResponse> => {
    const normalizedUsername = getRequiredTrimmedString(args.username, "Username");
    const normalizedPassword = getRequiredTrimmedString(
      args.newPassword,
      "New password",
    );
    const normalizedSession = getRequiredTrimmedString(args.session, "Session");

    const { userPoolClientId, userPoolRegion } = getCloudConfig();
    const providerClient = new CognitoIdentityProviderClient({
      region: userPoolRegion,
    });

    const response = await providerClient.send(
      new RespondToAuthChallengeCommand({
        ClientId: userPoolClientId,
        ChallengeName: "NEW_PASSWORD_REQUIRED",
        Session: normalizedSession,
        ChallengeResponses: {
          USERNAME: normalizedUsername,
          NEW_PASSWORD: normalizedPassword,
        },
      }),
    );

    const auth = response.AuthenticationResult;
    if (!auth?.AccessToken || !auth.IdToken) {
      throw new Error("Unable to complete password reset challenge.");
    }

    return finalizeAuthenticatedSession(
      auth.AccessToken,
      auth.IdToken,
      auth.RefreshToken,
    );
  },

  beginForgotPassword: async (username: string): Promise<{
    username: string;
    destination?: string;
    deliveryMedium?: string;
  }> => {
    const normalizedUsername = getRequiredTrimmedString(username, "Username");
    const { userPoolClientId, userPoolRegion } = getCloudConfig();
    const providerClient = new CognitoIdentityProviderClient({
      region: userPoolRegion,
    });

    const response = await providerClient.send(
      new ForgotPasswordCommand({
        ClientId: userPoolClientId,
        Username: normalizedUsername,
      }),
    );

    return {
      username: normalizedUsername,
      destination: response.CodeDeliveryDetails?.Destination,
      deliveryMedium: response.CodeDeliveryDetails?.DeliveryMedium,
    };
  },

  confirmForgotPassword: async (args: {
    username: string;
    confirmationCode: string;
    newPassword: string;
  }): Promise<void> => {
    const normalizedUsername = getRequiredTrimmedString(args.username, "Username");
    const normalizedCode = getRequiredTrimmedString(
      args.confirmationCode,
      "Confirmation code",
    );
    const normalizedPassword = getRequiredTrimmedString(
      args.newPassword,
      "New password",
    );
    const { userPoolClientId, userPoolRegion } = getCloudConfig();
    const providerClient = new CognitoIdentityProviderClient({
      region: userPoolRegion,
    });

    await providerClient.send(
      new ConfirmForgotPasswordCommand({
        ClientId: userPoolClientId,
        Username: normalizedUsername,
        ConfirmationCode: normalizedCode,
        Password: normalizedPassword,
      }),
    );
  },

  redeemRefreshToken: async (refreshToken: string): Promise<TokenResponse> => {
    const normalizedRefresh = getRequiredTrimmedString(
      refreshToken,
      "Refresh token",
    );

    const response = await authenticate("REFRESH_TOKEN_AUTH", {
      REFRESH_TOKEN: normalizedRefresh,
    });

    if ("challengeName" in response) {
      throw new Error("Unexpected auth challenge during token refresh.");
    }

    return response;
  },

  getAccessToken: (): string | null => {
    return readSession()?.accessToken ?? null;
  },

  getIdToken: (): string | null => {
    return readSession()?.idToken ?? null;
  },

  getCachedRefreshToken: (): string | null => {
    return readSession()?.refreshToken ?? null;
  },

  getCachedSubject: (): string | null => {
    return readSession()?.subject ?? null;
  },

  isAuthenticated: (): boolean => {
    const session = readSession();
    if (!session) {
      return false;
    }

    return session.accessExpiresAt > Date.now() || Boolean(session.refreshToken);
  },

  logout: async (): Promise<void> => {
    clearSession();
  },

  getAwsCredentials: async (): Promise<AwsCredentialIdentity> => {
    const session = readSession();
    if (!session) {
      throw new Error("Not authenticated.");
    }

    const active = isCredentialsValid(session)
      ? session
      : await refreshCredentialsFromRefreshToken();

    return {
      accessKeyId: active.credentials.accessKeyId,
      secretAccessKey: active.credentials.secretAccessKey,
      sessionToken: active.credentials.sessionToken,
    };
  },
};
